import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import {
  deliverySettingsApi,
  etaSettingsApi,
  multiorderParamsApi,
} from './search-settings-api';

/**
 * Refetch policy (shared by all three resources):
 *
 * - **active** — the form is seeded from it, so a background refetch must never
 *   replace values the admin is typing: no refetch on window focus (the
 *   app-wide default). It is still re-read on every page mount
 *   (`refetchOnMount: 'always'`) so the baseline is current, and a successful
 *   publish writes the new version straight into the cache instead of
 *   refetching it.
 * - **history** — read-only, so it keeps the app default (30s stale), holds the
 *   previous page while the next loads, and is invalidated by a publish
 *   (the old version's `end_at` changes too).
 */
function createResourceQueries<TVersion>(
  resource: string,
  api: {
    active: () => Promise<TVersion | null>;
    list: (params: {
      limit: number;
      skip: number;
    }) => Promise<{ list: TVersion[]; total: number; pageCount: number }>;
  },
) {
  const keys = {
    all: ['search-settings', resource] as const,
    active: () => ['search-settings', resource, 'active'] as const,
    histories: () => ['search-settings', resource, 'history'] as const,
    history: (page: number, pageSize: number) =>
      ['search-settings', resource, 'history', { page, pageSize }] as const,
  };
  const queries = {
    active: () =>
      queryOptions({
        queryKey: keys.active(),
        queryFn: () => api.active(),
        refetchOnMount: 'always',
      }),
    history: (page: number, pageSize: number) =>
      queryOptions({
        queryKey: keys.history(page, pageSize),
        queryFn: () => api.list({ limit: pageSize, skip: (page - 1) * pageSize }),
        placeholderData: keepPreviousData,
      }),
  };
  return { keys, queries };
}

export const deliverySettings = createResourceQueries('delivery', deliverySettingsApi);
export const etaSettings = createResourceQueries('eta', etaSettingsApi);
export const multiorderParams = createResourceQueries('multiorder', multiorderParamsApi);

export const deliverySettingsKeys = deliverySettings.keys;
export const deliverySettingsQueries = deliverySettings.queries;
export const etaSettingsKeys = etaSettings.keys;
export const etaSettingsQueries = etaSettings.queries;
export const multiorderParamsKeys = multiorderParams.keys;
export const multiorderParamsQueries = multiorderParams.queries;
